import React, { useState, useEffect } from 'react';
import { invokeRapidModuleManagementAPI } from '../../api/healthCheckAPI';

export default function ModuleManagement() {
  // --- STATE MANAGEMENT ---
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerInput, setCustomerInput] = useState("");
  const [availableClusters, setAvailableClusters] = useState([]);
  const [selectedClusterId, setSelectedClusterId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // New states for Multi-Site logic
  const [siteDataMap, setSiteDataMap] = useState({}); // Stores { siteName: { modules, mac, version... } }
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSiteName, setSelectedSiteName] = useState("");
  
  const [pendingModules, setPendingModules] = useState({}); // Local changes for the active site
  const [isLoading, setIsLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // --- HELPERS ---
  
  // Simulated parser: Expects API to return sites with their respective modules
  const processApiResponse = (response) => {
    const sites = response.sites || [
      {
        name: "Primary Data Center",
        macAddress: "00:50:56:B9:4C:12",
        platformVersion: "4.2.1",
        modules: ["AUTH:Cloud", "DB:Prem", "CACHE:Off"]
      },
      {
        name: "Recovery Site B",
        macAddress: "00:50:56:A1:22:FF",
        platformVersion: "4.2.0",
        modules: ["AUTH:Off", "DB:Prem", "CACHE:Prem"]
      }
    ];

    const map = {};
    sites.forEach(site => {
      map[site.name] = {
        ...site,
        parsedModules: parseModuleString(site.modules)
      };
    });
    return map;
  };

  const parseModuleString = (arr) => {
    const map = {};
    if (!arr || !Array.isArray(arr)) return map;

    arr.forEach(item => {
      if (typeof item === 'string' && item.includes(':')) {
        const [name, status] = item.split(':');
        if (name) {
          map[name.trim()] = {
            status: status ? status.trim() : "Off",
            version: "1.0.2",
            installDate: "2024-02-10"
          };
        }
      }
    });
    return map;
  };

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await fetch('./customers.json');
        const data = await response.json();
        setAllCustomers(data.customers);
      } catch (err) { console.error(err); }
    };
    loadCustomers();
  }, []);

  // Update pending modules when site selection changes
  useEffect(() => {
    if (selectedSiteName && siteDataMap[selectedSiteName]) {
      setPendingModules({ ...siteDataMap[selectedSiteName].parsedModules });
    }
  }, [selectedSiteName, siteDataMap]);

  const isDirty = hasQueried && JSON.stringify(pendingModules) !== JSON.stringify(siteDataMap[selectedSiteName]?.parsedModules);

  // Helper to compile only modified keys for popup verification
  const getChangedModules = () => {
    const original = siteDataMap[selectedSiteName]?.parsedModules || {};
    const changes = [];
    Object.keys(pendingModules).forEach(key => {
      if (pendingModules[key]?.status !== original[key]?.status) {
        changes.push({
          name: key,
          oldStatus: original[key]?.status || 'Off',
          newStatus: pendingModules[key]?.status
        });
      }
    });
    return changes;
  };

  // --- EVENT HANDLERS ---

  const handleFetchModules = async () => {
    if (!selectedClusterId) return;
    setIsLoading(true);
    try {
      const response = await invokeRapidModuleManagementAPI(selectedCustomer.id, selectedClusterId);
      const processed = processApiResponse(response);
      setSiteDataMap(processed);
      const siteNames = Object.keys(processed);
      setAvailableSites(siteNames);
      setSelectedSiteName(siteNames[0] || "");
      setHasQueried(true);
    } catch (err) {
      alert("Error fetching module status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleClick = (moduleName, newStatus) => {
    setPendingModules(prev => ({
      ...prev,
      [moduleName]: { ...prev[moduleName], status: newStatus }
    }));
  };

  const handleMasterToggle = (status) => {
    const updated = {};
    Object.keys(pendingModules).forEach(key => {
      updated[key] = { ...pendingModules[key], status: status };
    });
    setPendingModules(updated);
  };

  const handleSaveChanges = () => {
    // Commit the pending changes into our main dataset tracking
    setSiteDataMap(prev => ({
      ...prev,
      [selectedSiteName]: {
        ...prev[selectedSiteName],
        parsedModules: { ...pendingModules }
      }
    }));
    setShowConfirm(false);
  };

  const currentSiteInfo = siteDataMap[selectedSiteName] || {};

  // --- UI COMPONENTS ---
  const InfoItem = ({ label, value, isHighlight }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '10px', border: '1px solid #f0f0f0' }}>
      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: '600', color: isHighlight ? '#2980b9' : '#333' }}>{value || 'N/A'}</span>
    </div>
  );

  const TriStateToggle = ({ moduleName, currentStatus, onStatusChange, isMaster = false }) => {
    const states = ['Cloud', 'Off', 'Prem'];
    const colors = { Cloud: '#27ae60', Prem: '#2980b9', Off: '#7f8c8d' };
    return (
      <div style={{ display: 'flex', border: '1px solid #ccc', borderRadius: '20px', overflow: 'hidden', width: 'fit-content', backgroundColor: '#fff', margin: isMaster ? '0' : '0 auto' }}>
        {states.map((s) => (
          <div key={s} onClick={() => isMaster ? onStatusChange(s) : onStatusChange(moduleName, s)}
            style={{ padding: '6px 12px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: currentStatus === s ? colors[s] : 'transparent', color: currentStatus === s ? '#fff' : '#333', transition: '0.2s ease', borderRight: s !== 'Prem' ? '1px solid #eee' : 'none' }}>
            {s}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Segoe UI, sans-serif', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      
      {/* BANNER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#2c3e50', color: 'white', padding: '15px 25px', borderRadius: '8px', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>RAPID MODULE MANAGEMENT</h2>
        <button disabled={!hasQueried} style={{ padding: '8px 15px', backgroundColor: hasQueried ? '#f39c12' : '#7f8c8d', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}>
          EXPORT CHECKLIST
        </button>
      </div>

      {/* CLUSTER SELECTION */}
      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e0e0e0', display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '11px' }}>CUSTOMER</label>
          <input list="customers-list" style={{ width: '100%', height: '35px', padding: '0 10px', border: '1px solid #ddd', borderRadius: '4px' }} placeholder="Select Customer..." value={customerInput} 
            onChange={(e) => { 
              setCustomerInput(e.target.value); 
              const f = allCustomers.filter(c => c.name === e.target.value); 
              setAvailableClusters(f); 
              setHasQueried(false); 
            }} />
          <datalist id="customers-list">{[...new Set(allCustomers.map(c => c.name))].map(n => <option key={n} value={n} />)}</datalist>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '11px' }}>CLUSTER</label>
          <select style={{ width: '100%', height: '35px', padding: '0 10px', border: '1px solid #ddd', borderRadius: '4px' }} disabled={!availableClusters.length} value={selectedClusterId} 
            onChange={(e) => { 
              setSelectedClusterId(e.target.value); 
              setSelectedCustomer(availableClusters.find(c => c.clusterId === e.target.value)); 
              setHasQueried(false); 
            }}>
            <option value="">-- Choose Cluster --</option>
            {availableClusters.map(c => <option key={c.clusterId} value={c.clusterId}>{c.clusterName}</option>)}
          </select>
        </div>
        <button style={{ height: '35px', padding: '0 25px', backgroundColor: (selectedClusterId && !isLoading) ? '#27ae60' : '#bdc3c7', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }} onClick={handleFetchModules}>
          {isLoading ? "FETCHING..." : "GET STATUS"}
        </button>
      </div>

      {hasQueried && (
        <div style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          
          {/* 2x4 GRID SECTION */}
          <div style={{ padding: '20px', backgroundColor: '#fcfcfc', borderBottom: '1px solid #eee' }}>
            <h4 style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#2c3e50', borderLeft: '4px solid #27ae60', paddingLeft: '10px' }}>SERVER CONFIGURATION</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0', border: '1px solid #eee' }}>
              <InfoItem label="Customer Name" value={selectedCustomer?.name} />
              <InfoItem label="Site Name (Active)" value={selectedSiteName} />
              <InfoItem label="Cluster ID" value={selectedClusterId} />
              <InfoItem label="Cluster Name" value={selectedCustomer?.clusterName} />
              
              <InfoItem label="Server UUID" value={selectedCustomer?.id} />
              <InfoItem label="MAC Address" value={currentSiteInfo.macAddress} />
              <InfoItem label="Platform Version" value={currentSiteInfo.platformVersion} isHighlight />
              <InfoItem label="Last Sync" value={new Date().toLocaleDateString()} />
            </div>
          </div>

          {/* SITE SELECTOR & MODULES TABLE */}
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', backgroundColor: '#ebf5fb', padding: '15px', borderRadius: '8px', border: '1px solid #d4e6f1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '13px', color: '#2980b9' }}>SELECT SITE:</label>
                <select 
                  style={{ height: '35px', padding: '0 10px', borderRadius: '4px', border: '1px solid #2980b9', minWidth: '200px' }}
                  value={selectedSiteName}
                  onChange={(e) => setSelectedSiteName(e.target.value)}
                >
                  {availableSites.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#666' }}>BULK APPLY:</span>
                <TriStateToggle currentStatus={null} onStatusChange={handleMasterToggle} isMaster />
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', color: '#95a5a6', fontSize: '11px' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>MODULE NAME</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>VERSION</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>INSTALL DATE</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pendingModules).map(([name, data]) => (
                  <tr key={name} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '12px', fontWeight: 'bold', color: '#34495e', fontSize: '13px', textAlign: 'left' }}>{name.toUpperCase()}</td>
                    <td style={{ padding: '12px', color: '#7f8c8d', fontSize: '12px', textAlign: 'center' }}>{data.version}</td>
                    <td style={{ padding: '12px', color: '#7f8c8d', fontSize: '12px', textAlign: 'center' }}>{data.installDate}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <TriStateToggle moduleName={name} currentStatus={data.status} onStatusChange={handleToggleClick} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <button onClick={() => setPendingModules({...siteDataMap[selectedSiteName].parsedModules})} disabled={!isDirty} style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', fontSize: '12px' }}>RESET SITE</button>
              <button onClick={() => setShowConfirm(true)} disabled={!isDirty || isLoading} style={{ padding: '8px 25px', backgroundColor: isDirty ? '#27ae60' : '#bdc3c7', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>SAVE SITE CHANGES</button>
            </div>
          </div>
        </div>
      )}

      {/* --- CONFIRMATION POPUP OVERLAY --- */}
      {showConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '8px', width: '450px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '16px' }}>Confirm Deployment Strategy Changes</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
              You are updating configurations for site: <strong style={{ color: '#2980b9' }}>{selectedSiteName}</strong>. Review your modifications below:
            </p>
            
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', marginBottom: '20px', backgroundColor: '#fafafa' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#eee', color: '#555', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Module</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>From</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>To</th>
                  </tr>
                </thead>
                <tbody>
                  {getChangedModules().map(mod => (
                    <tr key={mod.name} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>{mod.name.toUpperCase()}</td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#e74c3c' }}>{mod.oldStatus}</td>
                      <td style={{ padding: '8px', textAlign: 'center', color: '#27ae60', fontWeight: 'bold' }}>{mod.newStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: '8px 15px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px' }}>
                Cancel
              </button>
              <button onClick={handleSaveChanges} style={{ padding: '8px 20px', borderRadius: '4px', border: 'none', backgroundColor: '#27ae60', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}